export const queryParams = <T extends Record<string, undefined | null | string | number | boolean>>(params: T): T => {
  return Object.fromEntries(
    Object.entries(params).reduce((acc, [key, value]) => {
      if (value === undefined || value === null || (typeof value === 'string' && value.trim().length === 0) || (typeof value === 'number' && Number.isNaN(value))) {
        return acc;
      }

      return [...acc, [key, value]];
    }, [] as [string, undefined | string | number | boolean][]),
  ) as T;
};

export interface TablePagination {
  page: number;
  limit: number;
}
export interface ApiPagination {
  offset?: number;
  limit?: number;
}
interface DefaultPaginationParams {
  limit?: number;
  offset?: number;
  search?: string;
  sort?: string;
}
export type BaseTableParams<T extends ApiPagination = DefaultPaginationParams> = Omit<T, 'offset' | 'limit'> & TablePagination;

/**
 * Converts table pagination data to API format
 * @param page Current table page
 * @param limit Current table limit
 * @param params Other table params (sorting, search, ...)
 * @param removeEmpty Removes empty values when true (null, undefined, empty string and NaN)
 * @returns Converted table pagination to API format (page converted to offset)
 */
export const paginationToApiFormat = <T extends TablePagination>({ page, limit, ...params }: T, removeEmpty = false): Omit<T, 'page' | 'limit'> & ApiPagination => {
  const formattedParams = {
    offset: limit * (page - 1),
    limit,
    ...params,
  };

  return removeEmpty ? queryParams(formattedParams) : formattedParams;
};
